import { DatePickerInput, Modal } from '@sequencehq/core-components'
import { Footer } from './common/Footer'
import { add, format } from '@sequencehq/utils/dates'
import { Box, Grid } from '@chakra-ui/react'
import {
  GreyGrey60,
  GreyGrey80,
  Lato12Regular,
  Lato13Bold
} from '@sequencehq/design-tokens'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/validation'
import { cubeUpdateShortcuts } from 'Cube/index'
import { match } from 'ts-pattern'
import { useNavigate } from 'react-router-dom'
import { RadioGroup } from '../../../common/RadioGroup'
import { useNextScheduleBillingDate } from '../utils/useNextScheduleBillingDate'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { useScheduleHasFuturePhases } from '../utils/useHasFuturePhases'
import { DATE_FORMAT } from '../editModalShortcuts.constants'

type UpgradeDateType = 'nextBillingDate' | 'customDate' | 'immediate'
type FormData = {
  upgradeDateType: UpgradeDateType
  customDate: Date | undefined
}

const useUpgradeScreen = () => {
  const navigate = useNavigate()
  const billingScheduleId = useBillingScheduleId()
  const nextBillingDateQuery = useNextScheduleBillingDate({
    billingScheduleId
  })
  const futurePhasesQuery = useScheduleHasFuturePhases({
    billingScheduleId
  })

  const form = useForm<FormData>({
    value: {
      upgradeDateType: futurePhasesQuery.hasFuturePhases
        ? 'customDate'
        : 'immediate',
      customDate: undefined
    },
    fieldConfiguration: [
      {
        property: 'upgradeDateType',
        validation: [required]
      },
      {
        property: 'customDate',
        disabled: ctx => ctx.formData.upgradeDateType !== 'customDate',
        validation: [required]
      }
    ]
  })

  const onSubmit = () => {
    if (!form.queries.isValid) {
      return
    }

    const startDate =
      match(form.queries.formData.upgradeDateType)
        .with('immediate', () => new Date())
        .with('nextBillingDate', () => nextBillingDateQuery.nextBillingDate)
        .with('customDate', () => form.queries.formData.customDate)
        .exhaustive() ?? new Date()

    const shortcutUuid = cubeUpdateShortcuts.set({
      type: 'upgrade',
      startDate: startDate
    })

    navigate(
      `/billing-schedules/${billingScheduleId}/edit?${cubeUpdateShortcuts.queryParam}=${shortcutUuid}`
    )
  }

  return {
    isLoading: nextBillingDateQuery.isLoading || futurePhasesQuery.isLoading,
    nextBillingDate: nextBillingDateQuery.nextBillingDate,
    hasFuturePhases: futurePhasesQuery.hasFuturePhases,
    formFields: form.fields,
    isValid: form.queries.isValid,
    minimumCustomDate: futurePhasesQuery.lastPhaseStartDate
      ? add(futurePhasesQuery.lastPhaseStartDate, { days: 1 })
      : new Date(),
    onSubmit
  }
}

export const UpgradeScreen = () => {
  const {
    isLoading,
    nextBillingDate,
    formFields,
    isValid,
    onSubmit,
    hasFuturePhases,
    minimumCustomDate
  } = useUpgradeScreen()

  return (
    <>
      <Modal.Content>
        <Grid gridTemplateRows="auto auto auto" gap="12px">
          <Box {...Lato13Bold} color={GreyGrey80}>
            When should changes take effect?
          </Box>
          <RadioGroup
            name="upgradeDateType"
            onChange={newValue =>
              formFields.upgradeDateType.onChange(
                newValue as FormData['upgradeDateType']
              )
            }
            value={formFields.upgradeDateType.value}
            options={[
              {
                label: 'Immediately',
                description: 'Changes take effect immediately.',
                labelAnnotation:
                  formFields.upgradeDateType.value === 'immediate'
                    ? format(new Date(), DATE_FORMAT)
                    : '',
                value: 'immediate',
                disabled: hasFuturePhases,
                disabledReason: hasFuturePhases
                  ? 'Cannot upgrade a schedule when there are phases starting in the future'
                  : undefined
              },
              {
                label: 'Next billing period',
                description:
                  'Changes take effect at the start of the next billing period.',
                labelAnnotation:
                  formFields.upgradeDateType.value === 'nextBillingDate' &&
                  nextBillingDate
                    ? format(nextBillingDate, DATE_FORMAT)
                    : '',
                value: 'nextBillingDate',
                disabled: !nextBillingDate || hasFuturePhases,
                disabledReason: match({
                  hasFuturePhases,
                  nextBillingDate,
                  isLoading
                })
                  .with(
                    { hasFuturePhases: true },
                    () =>
                      'Cannot upgrade a schedule when there are phases starting in the future'
                  )
                  .with(
                    { nextBillingDate: null },
                    () => 'Could not calculate next billing date'
                  )
                  .otherwise(() => undefined)
              },
              {
                label: 'Custom',
                description: 'Changes take effect on a custom date.',
                value: 'customDate',
                additionalComponent:
                  formFields.upgradeDateType.value === 'customDate' ? (
                    <Box paddingTop="12px">
                      <DatePickerInput
                        minDate={minimumCustomDate}
                        width="180px"
                        value={formFields.customDate.value}
                        onChange={newDate =>
                          formFields.customDate.onChange(newDate)
                        }
                      />
                    </Box>
                  ) : null
              }
            ]}
          />
          <Box color={GreyGrey60} {...Lato12Regular}>
            Creates a new phase where you can make price changes.
          </Box>
        </Grid>
      </Modal.Content>
      <Footer onContinue={onSubmit} disabled={!isValid} />
    </>
  )
}
